import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

interface Props {
  personName: string;
  consentConfirmed: boolean;
  onConsentChange: (confirmed: boolean) => void;
  sampleLabel: string;
  onSampleLabelChange: (label: string) => void;
  disabled?: boolean;
}

/** The recording-condition label and the explicit consent confirmation.
 *
 * Consent is a checkbox the admin has to tick, never a pre-checked default:
 * the backend refuses the request without it, and the version of this notice
 * is stored alongside the sample so it stays clear what was agreed to. */
export const VoiceSampleConsent = ({
  personName,
  consentConfirmed,
  onConsentChange,
  sampleLabel,
  onSampleLabelChange,
  disabled = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <div className="space-y-3">
      <div
        className="flex items-start gap-2 rounded-md p-3"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1">
          <Text as="p" size="sm" weight="medium">
            {t('voice-enrolling-for', { name: personName })}
          </Text>
          <Text as="p" size="xs" tone="muted">
            {t('voice-consent-notice')}
          </Text>
        </div>
      </div>

      {/* `id` gives the atom's <label htmlFor> something to point at, so the
          field has an accessible name. */}
      <Input
        id="voice-sample-label"
        value={sampleLabel}
        onChange={event => onSampleLabelChange(event.target.value)}
        label={t('voice-sample-label')}
        placeholder={t('voice-sample-label-placeholder')}
        helperText={t('voice-sample-label-helper')}
        disabled={disabled}
        maxLength={128}
      />

      <Checkbox
        checked={consentConfirmed}
        onChange={event => onConsentChange(event.target.checked)}
        disabled={disabled}
        label={t('voice-consent-confirm-label')}
        description={t('voice-consent-confirm-description')}
      />
    </div>
  );
};
